/**
 * Ranking for ⌘K (designs 29b, 29c), kept free of React so it can be tested.
 *
 * - A contiguous match beats a scattered one: whole title, then title start,
 *   then a word start, then anywhere, then every word somewhere. Fuzzy hits
 *   only rescue typos, when nothing matches contiguously; otherwise they'd be
 *   rows with nothing tinted.
 * - Typing a task ID ("START-10"), or just its number inside a project, puts
 *   that task first.
 * - Results group by type, groups ordered by their best hit, so the top hit
 *   leads. "All" caps each group and says how many more there are.
 */
export type SearchItemType = 'task' | 'project' | 'page' | 'note'
export type SearchFilter = 'all' | 'tasks' | 'projects' | 'pages' | 'notes'

export interface SearchItem {
  id: string
  name: string
  type: SearchItemType
  projectKey?: string
  projectName?: string
  slug?: string
  projectId?: string
  icon?: string | null
  iconType?: string | null
  iconColor?: string | null
  noteType?: 'daily' | 'weekly'
  noteDate?: string
  /** Task: its friendly ID, e.g. "START-10". */
  identifier?: string
  seqNo?: number | null
  statusId?: string | null
  progressType?: string
  /** The space the item sits in; null for Inbox tasks and notes. */
  spaceId?: string | null
  spaceName?: string
  /** Project: its parent; null when the project is itself a space. */
  parentProjectId?: string | null
  updatedOn?: number
}

export interface SearchScope {
  kind: 'space' | 'project'
  id: string
  name: string
  icon?: string | null
  iconType?: string | null
  iconColor?: string | null
}

export interface RankedItem {
  item: SearchItem
  score: number
}

export interface SearchGroup {
  type: SearchItemType
  items: Array<RankedItem>
  /** Hits in the group before the cap. */
  total: number
}

export const FILTER_TYPE: Record<Exclude<SearchFilter, 'all'>, SearchItemType> = {
  tasks: 'task',
  projects: 'project',
  pages: 'page',
  notes: 'note',
}

const TYPE_ORDER: Array<SearchItemType> = ['project', 'task', 'page', 'note']
const DONE_PROGRESS = new Set(['CD', 'CL'])

/**
 * A space scope keeps what sits in that space plus the things that are only
 * yours (Inbox tasks, notes). A project scope keeps that project's tasks and
 * docs.
 */
export function inScope(item: SearchItem, scope: SearchScope | null): boolean {
  if (!scope) return true
  if (scope.kind === 'project') {
    return item.projectId === scope.id && (item.type === 'task' || item.type === 'page')
  }
  if (item.type === 'note') return true
  if (item.type === 'task' && !item.projectId) return true
  if (item.type === 'project') return item.id !== scope.id && item.spaceId === scope.id
  return item.spaceId === scope.id
}

const ID_RE = /^([a-z][a-z0-9]*)-(\d+)$/i
const NUMBER_RE = /^#?(\d+)$/

/** The task a query names by ID, if any. */
export function matchTaskId(
  items: ReadonlyArray<SearchItem>,
  query: string,
  projectId: string | null,
): SearchItem | null {
  const q = query.trim()
  if (ID_RE.test(q)) {
    const lower = q.toLowerCase()
    return items.find((i) => i.type === 'task' && i.identifier?.toLowerCase() === lower) ?? null
  }
  const number = NUMBER_RE.exec(q)
  if (number && projectId) {
    const seq = Number(number[1])
    return (
      items.find((i) => i.type === 'task' && i.projectId === projectId && i.seqNo === seq) ??
      null
    )
  }
  return null
}

function isWordStart(text: string, index: number): boolean {
  return index === 0 || !/[\p{L}\p{N}]/u.test(text[index - 1] ?? '')
}

/** Lower is better; null means no contiguous match. */
export function contiguousScore(name: string, query: string): number | null {
  const text = name.toLowerCase()
  const q = query.trim().toLowerCase()
  if (!q) return null
  if (text === q) return 0
  const index = text.indexOf(q)
  if (index === 0) return 0.05
  if (index > 0) return isWordStart(text, index) ? 0.1 : 0.2
  const terms = q.split(/\s+/).filter(Boolean)
  if (terms.length > 1 && terms.every((term) => text.includes(term))) return 0.3
  return null
}

/**
 * Where to tint a title for a query: the whole query where it appears, else
 * each word of two letters or more. Never scattered letters.
 */
export function highlightRanges(text: string, query: string): Array<[number, number]> {
  const lower = text.toLowerCase()
  const q = query.trim().toLowerCase()
  if (!q) return []
  const whole = lower.indexOf(q)
  if (whole >= 0) return [[whole, whole + q.length]]

  const ranges: Array<[number, number]> = []
  for (const term of q.split(/\s+/)) {
    if (term.length < 2) continue
    const at = lower.indexOf(term)
    if (at >= 0) ranges.push([at, at + term.length])
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged: Array<[number, number]> = []
  for (const range of ranges) {
    const last = merged[merged.length - 1]
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1])
    else merged.push([...range])
  }
  return merged
}

export interface RankOptions {
  scope: SearchScope | null
  /** The project on screen; a bare number jumps to its task. */
  routeProjectId: string | null
  /** Fuzzy scores (0 best, 1 worst) for typo-tolerant fallbacks. */
  fuzzy?: ReadonlyMap<string, number>
}

export function rankSearch(
  items: ReadonlyArray<SearchItem>,
  query: string,
  { scope, routeProjectId, fuzzy }: RankOptions,
): Array<RankedItem> {
  const q = query.trim()
  if (!q) return []
  const scoped = items.filter((item) => inScope(item, scope))
  const idHit = matchTaskId(scoped, q, scope?.kind === 'project' ? scope.id : routeProjectId)

  const collect = (scoreOf: (item: SearchItem) => number | null) => {
    const ranked: Array<RankedItem> = []
    for (const item of scoped) {
      let score = item === idHit ? -1 : scoreOf(item)
      if (score === null) continue
      // Finished tasks sink below open ones that match as well.
      if (item.type === 'task' && item.progressType && DONE_PROGRESS.has(item.progressType)) {
        score += 0.04
      }
      ranked.push({ item, score })
    }
    return ranked
  }

  let ranked = collect((item) => contiguousScore(item.name, q))
  if (ranked.length === 0 && fuzzy) {
    ranked = collect((item) => {
      const fuzzyScore = fuzzy.get(`${item.type}:${item.id}`)
      return fuzzyScore === undefined ? null : 0.4 + fuzzyScore
    })
  }

  return ranked.sort(
    (a, b) => a.score - b.score || (b.item.updatedOn ?? 0) - (a.item.updatedOn ?? 0),
  )
}

export function countByType(ranked: ReadonlyArray<RankedItem>): Record<SearchFilter, number> {
  const counts: Record<SearchFilter, number> = { all: ranked.length, tasks: 0, projects: 0, pages: 0, notes: 0 }
  for (const { item } of ranked) {
    if (item.type === 'task') counts.tasks++
    else if (item.type === 'project') counts.projects++
    else if (item.type === 'page') counts.pages++
    else counts.notes++
  }
  return counts
}

export function groupResults(
  ranked: ReadonlyArray<RankedItem>,
  filter: SearchFilter,
  cap: number,
): Array<SearchGroup> {
  const byType = new Map<SearchItemType, Array<RankedItem>>()
  for (const entry of ranked) {
    if (filter !== 'all' && entry.item.type !== FILTER_TYPE[filter]) continue
    const list = byType.get(entry.item.type) ?? []
    list.push(entry)
    byType.set(entry.item.type, list)
  }
  return [...byType.entries()]
    .map(([type, list]) => ({ type, items: list.slice(0, cap), total: list.length }))
    .sort(
      (a, b) =>
        (a.items[0]?.score ?? 0) - (b.items[0]?.score ?? 0) ||
        TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type),
    )
}
